import { Link } from 'react-router-dom';
import {
  Code,
  ArrowRight,
  Heart,
  ExternalLink,
} from 'lucide-react';
import PublicNav from '../../components/PublicNav';
import PublicFooter from '../../components/PublicFooter';
import BackToTop from '../../components/ui/BackToTop';
import {
  showcaseProjects,
  getStatusLabel,
  getStatusColor,
} from '../../data/showcaseProjects';

const PublicProjectsShowcase = () => {
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <main id="main-content">
        {/* Hero */}
        <section
          aria-labelledby="projects-heading"
          className="relative bg-secondary-950 py-20 sm:py-28 overflow-hidden"
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-15 animate-gradient-shift"
            style={{
              backgroundImage:
                'radial-gradient(circle at 30% 40%, rgba(53,94,59,0.4) 0%, transparent 50%), radial-gradient(circle at 70% 60%, rgba(53,94,59,0.2) 0%, transparent 45%)',
              backgroundSize: '200% 200%',
            }}
          />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary-900/30 backdrop-blur-sm border border-primary-700/20 px-4 py-1.5 text-sm font-medium text-primary-300 mb-6">
              <Code className="h-4 w-4" />
              Open Source &middot; Justice Tech
            </div>
            <h1
              id="projects-heading"
              className="font-display text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight tracking-tight"
            >
              What We're Building
            </h1>
            <p className="text-lg sm:text-xl text-secondary-300 leading-relaxed max-w-2xl mx-auto">
              Every project addresses a real gap in the justice-to-tech pipeline.
              From AI-powered learning tools to reentry resource platforms — this is
              where your support becomes code.
            </p>
          </div>
        </section>

        {/* Project Cards */}
        <section aria-label="Project roster" className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              {showcaseProjects.map((project) => (
                <article
                  key={project.slug}
                  className="bg-white rounded-xl border border-secondary-200 overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="p-8 sm:p-10">
                    {/* Header row */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <h2 className="text-2xl font-bold text-secondary-900">
                        <Link
                          to={`/projects/${project.slug}`}
                          className="hover:text-primary-600 no-underline hover:no-underline transition-colors"
                        >
                          {project.title}
                        </Link>
                      </h2>
                      <span
                        className={`inline-flex items-center self-start px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(
                          project.status
                        )}`}
                      >
                        {getStatusLabel(project.status)}
                      </span>
                    </div>

                    {/* Summary */}
                    <p className="text-secondary-700 leading-relaxed mb-6">
                      {project.summary}
                    </p>

                    {/* Tech stack */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {project.techStack.map((tech) => (
                        <span
                          key={tech}
                          className="bg-secondary-100 text-secondary-700 text-xs font-medium px-2.5 py-1 rounded-md"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    {/* Impact goal */}
                    <div className="bg-primary-50 border border-primary-100 rounded-lg p-4 mb-6">
                      <p className="text-sm font-semibold text-primary-800 mb-1">
                        Impact Goal
                      </p>
                      <p className="text-sm text-primary-700 leading-relaxed">
                        {project.impactGoal}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-3">
                      <Link
                        to={`/projects/${project.slug}`}
                        className="btn bg-primary-600 text-white hover:bg-primary-500 inline-flex items-center no-underline hover:no-underline text-sm"
                      >
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                      {project.demoUrl && (
                        <a
                          href={project.demoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn border border-secondary-300 text-secondary-700 hover:bg-secondary-50 inline-flex items-center no-underline hover:no-underline text-sm"
                        >
                          Live Demo
                          <ExternalLink className="ml-2 h-3.5 w-3.5 opacity-60" />
                        </a>
                      )}
                      {project.repoUrl && (
                        <a
                          href={project.repoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn border border-secondary-300 text-secondary-700 hover:bg-secondary-50 inline-flex items-center no-underline hover:no-underline text-sm"
                        >
                          Source Code
                          <ExternalLink className="ml-2 h-3.5 w-3.5 opacity-60" />
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          aria-labelledby="fund-heading"
          className="py-16 bg-gradient-hero"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2
              id="fund-heading"
              className="text-3xl font-bold text-white mb-4"
            >
              Fund This Work
            </h2>
            <p className="text-lg text-secondary-300 mb-8 leading-relaxed">
              Every project on this page is built by and for the community we serve.
              Your support keeps the servers running and the curriculum growing.
            </p>
            <Link
              to="/donate"
              className="btn btn-lg bg-primary-600 text-white hover:bg-primary-500 inline-flex items-center shadow-md no-underline hover:no-underline"
            >
              <Heart className="mr-2 h-5 w-5" />
              Support Our Mission
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>

      <PublicFooter />
      <BackToTop />
    </div>
  );
};

export default PublicProjectsShowcase;
